import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, PencilIcon, TrashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ShieldCheckIcon } from '@heroicons/react/24/solid';
import { toast } from '../components/shared/Toast';
import {
  useGuardrails,
  type GuardrailRule,
  type CreateRulePayload,
} from '../hooks/useGuardrails';

// ---------------------------------------------------------------------------
// Rule form (create / edit)
// ---------------------------------------------------------------------------

type RuleFormProps = {
  initial?: Partial<GuardrailRule>;
  onSave: (data: CreateRulePayload) => Promise<void>;
  onCancel: () => void;
};

function RuleForm({ initial, onSave, onCancel }: RuleFormProps) {
  const { t } = useTranslation();
  const [category, setCategory] = useState(initial?.category ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [pattern, setPattern] = useState(initial?.pattern ?? '');
  const [severity, setSeverity] = useState<'block' | 'warn'>(initial?.severity ?? 'block');
  const [appliesTo, setAppliesTo] = useState<'input' | 'output' | 'both'>(initial?.applies_to ?? 'both');
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const [patternError, setPatternError] = useState('');

  const validatePattern = (p: string) => {
    try { new RegExp(p); setPatternError(''); return true; }
    catch (e) { setPatternError(String(e)); return false; }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePattern(pattern)) return;
    setSaving(true);
    try {
      await onSave({ category, label, pattern, severity, applies_to: appliesTo, is_active: isActive });
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm text-app-text focus:border-app-accent focus:outline-none focus:ring-2 focus:ring-app-accent/40';
  const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-app-text-dim';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>{t('guardrails.form.category')}</label>
          <input className={inputCls} value={category} onChange={e => setCategory(e.target.value)} required />
        </div>
        <div>
          <label className={labelCls}>{t('guardrails.form.label')}</label>
          <input className={inputCls} value={label} onChange={e => setLabel(e.target.value)} required />
        </div>
      </div>
      <div>
        <label className={labelCls}>{t('guardrails.form.pattern')}</label>
        <input
          className={`${inputCls} font-mono ${patternError ? 'border-app-error' : ''}`}
          value={pattern}
          onChange={e => { setPattern(e.target.value); validatePattern(e.target.value); }}
          required
          placeholder="(?i)\bkeyword\b"
        />
        {patternError && <p className="mt-1 text-xs text-app-error">{patternError}</p>}
        <p className="mt-1 text-xs text-app-text-dim">{t('guardrails.form.patternHint')}</p>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>{t('guardrails.form.severity')}</label>
          <select className={inputCls} value={severity} onChange={e => setSeverity(e.target.value as 'block' | 'warn')}>
            <option value="block">{t('guardrails.form.severityBlock')}</option>
            <option value="warn">{t('guardrails.form.severityWarn')}</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>{t('guardrails.form.appliesTo')}</label>
          <select className={inputCls} value={appliesTo} onChange={e => setAppliesTo(e.target.value as 'input' | 'output' | 'both')}>
            <option value="both">{t('guardrails.form.appliesToBoth')}</option>
            <option value="input">{t('guardrails.form.appliesToInput')}</option>
            <option value="output">{t('guardrails.form.appliesToOutput')}</option>
          </select>
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-sm text-app-text cursor-pointer">
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 rounded" />
            {t('guardrails.form.isActive')}
          </label>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="rounded-lg border border-app-border px-4 py-2 text-sm hover:bg-app-muted">
          {t('guardrails.form.cancel')}
        </button>
        <button type="submit" disabled={saving} className="rounded-lg bg-app-accent px-4 py-2 text-sm font-semibold text-white hover:bg-app-accent/90 disabled:opacity-50">
          {saving ? '…' : t('guardrails.form.save')}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function GuardrailsPage() {
  const { t } = useTranslation();
  const { rules, loading, error, createRule, updateRule, deleteRule, toggleRule } = useGuardrails();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<GuardrailRule | null>(null);

  const severityBadge = (s: string) =>
    s === 'block'
      ? 'bg-app-error/15 text-app-error'
      : 'bg-app-warning/15 text-app-warning';

  const appliesToBadge = (a: string) =>
    a === 'both'
      ? 'bg-app-accent/10 text-app-accent'
      : 'bg-app-muted text-app-text-sec';

  const handleCreate = async (data: CreateRulePayload) => {
    await createRule(data);
    toast.success(t('guardrails.ruleAdded'));
    setShowCreate(false);
  };

  const handleUpdate = async (data: CreateRulePayload) => {
    if (!editing) return;
    await updateRule(editing.id, data);
    toast.success(t('guardrails.ruleUpdated'));
    setEditing(null);
  };

  const handleDelete = async (rule: GuardrailRule) => {
    if (!window.confirm(t('guardrails.confirmDelete'))) return;
    await deleteRule(rule.id);
    toast.success(t('guardrails.ruleDeleted'));
  };

  const handleToggle = async (rule: GuardrailRule) => {
    await toggleRule(rule.id, !rule.is_active);
    toast.success(t('guardrails.ruleUpdated'));
  };

  return (
    <div className="mx-auto max-w-screen-xl p-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <ShieldCheckIcon className="h-8 w-8 text-app-accent" />
          <div>
            <h1 className="text-xl font-bold text-app-text">{t('guardrails.pageTitle')}</h1>
            <p className="mt-0.5 text-sm text-app-text-sec">{t('guardrails.pageSubtitle')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setShowCreate(true); setEditing(null); }}
          className="flex items-center gap-2 rounded-lg bg-app-accent px-4 py-2 text-sm font-semibold text-white hover:bg-app-accent/90"
        >
          <PlusIcon className="h-4 w-4" />
          {t('guardrails.addRule')}
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="mb-6 rounded-xl border border-app-accent/30 bg-app-surface p-6 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold text-app-text">{t('guardrails.addRule')}</h2>
          <RuleForm onSave={handleCreate} onCancel={() => setShowCreate(false)} />
        </div>
      )}

      {/* Edit form */}
      {editing && (
        <div className="mb-6 rounded-xl border border-app-accent/30 bg-app-surface p-6 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold text-app-text">Edit — {editing.label}</h2>
          <RuleForm initial={editing} onSave={handleUpdate} onCancel={() => setEditing(null)} />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 rounded-lg border border-app-error/20 bg-app-error/10 px-4 py-3 text-sm text-app-error">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && <p className="text-sm text-app-text-dim">Loading…</p>}

      {/* Table */}
      {!loading && (
        <div className="overflow-hidden rounded-xl border border-app-border/80 bg-app-surface shadow-sm">
          {rules.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-app-text-dim">
              <ShieldCheckIcon className="h-10 w-10 opacity-30" />
              <p className="text-sm">{t('guardrails.noRules')}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-app-border/60 bg-app-muted/30">
                <tr>
                  {(['id','category','label','pattern','severity','appliesTo','status','actions'] as const).map(k => (
                    <th key={k} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-app-text-dim">
                      {t(`guardrails.table.${k}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-app-border/40">
                {rules.map(rule => (
                  <tr key={rule.id} className={`transition-colors hover:bg-app-muted/20 ${!rule.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3 font-mono text-xs text-app-text-dim">{rule.id}</td>
                    <td className="px-4 py-3">
                      <span className="rounded bg-app-muted px-2 py-0.5 text-xs font-medium text-app-text-sec">{rule.category}</span>
                    </td>
                    <td className="px-4 py-3 font-medium text-app-text">{rule.label}</td>
                    <td className="max-w-[200px] truncate px-4 py-3 font-mono text-xs text-app-text-dim" title={rule.pattern}>{rule.pattern}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${severityBadge(rule.severity)}`}>
                        {t(`guardrails.severity.${rule.severity}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${appliesToBadge(rule.applies_to)}`}>
                        {t(`guardrails.appliesTo.${rule.applies_to}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => void handleToggle(rule)}
                        className="flex items-center gap-1.5 text-xs font-medium"
                        title={rule.is_active ? 'Click to disable' : 'Click to enable'}
                      >
                        {rule.is_active
                          ? <><CheckCircleIcon className="h-4 w-4 text-app-success" /> <span className="text-app-success">{t('guardrails.status.active')}</span></>
                          : <><XCircleIcon className="h-4 w-4 text-app-text-dim" /> <span className="text-app-text-dim">{t('guardrails.status.inactive')}</span></>
                        }
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => { setEditing(rule); setShowCreate(false); }}
                          className="rounded p-1.5 text-app-text-dim hover:bg-app-muted hover:text-app-accent"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(rule)}
                          className="rounded p-1.5 text-app-text-dim hover:bg-app-error/10 hover:text-app-error"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
